import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { normalizeClinicId } from '../utils/clinicIdUtils';
import { buildApiUrl } from '../services/apiHelpers';

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const requestSequence = useRef(0);

  const { auth } = useAuth();
  const rawClinicInfo = auth?.clinic || (typeof window !== 'undefined' && window.__CLINIC_ID__ ? { id: window.__CLINIC_ID__ } : null);
  const clinicId = normalizeClinicId(rawClinicInfo?.id);
  const clinicInfo = clinicId ? { ...rawClinicInfo, id: clinicId } : null;

  const fetchSubscriptionData = useCallback(async () => {
    const requestId = ++requestSequence.current;

    if (!clinicId) {
      setSubscriptionData(null);
      setLoading(false);
      return null;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        buildApiUrl(`subscription/dashboard/${clinicId}`),
        {
          headers: { 'X-Clinic-Id': clinicId },
          withCredentials: true,
        }
      );
      const data = response.data;
      const mode = String(data?.mode || '').trim().toUpperCase();
      const normalizedData = data && typeof data === 'object' ? {
        ...data,
        mode: mode === 'ACTIVE' ? 'SUBSCRIBED' : mode === 'PENDING' ? 'PENDING_APPROVAL' : mode,
      } : data;

      if (requestId === requestSequence.current) {
        setSubscriptionData(normalizedData);
      }
      return normalizedData;
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      if (requestId === requestSequence.current) {
        setSubscriptionData(null);
      }
      return null;
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false);
      }
    }
  }, [clinicId]);

  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  const refreshSubscription = fetchSubscriptionData;

  const value = {
    subscriptionData,
    setSubscriptionData,
    loading,
    setLoading,
    refreshSubscription,
    clinicInfo,
    clinicId,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
